import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import NumberField from '../../components/Fields/NumberField';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import WorkflowActionModel from '../../models/WorkflowActionModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import Action_tab_CodeTasks from './Tabs/Action_tab_CodeTasks';

const WorkflowActionView: React.FC = () => {
    const [formData, setFormData] = useState<WorkflowActionModel>();
    const [serverErrors, setServerErrors] = useState<DataResult<WorkflowActionModel>>({ isSuccesful: true, messages: [], data: undefined });
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const { handleResult, handleException } = useNotifications();
    const navigate = useNavigate()

    const workflowTaskDefinitions = useSelectListOptions('api/v1/selectlist/WorkflowTaskDefinitions?workflowStepDefinitionId=' + formData?.workflowStepDefinitionId ?? 0, '-- All tasks');
    const workflowStepDefinitions = useSelectListOptions('api/v1/selectlist/WorkflowStepDefinitions?workflowDefinitionId=' + formData?.workflowStepDefinition?.workflowDefinitionId, '-- Select step', undefined, formData?.workflowStepDefinition?.workflowDefinitionId != undefined);

    let idParams: IdParams = useParams();
    let workflowActionId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        workflowActionId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (workflowActionId !== undefined) {
            DataService.getById<WorkflowActionModel>('api/v1/WorkflowActions/', workflowActionId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [workflowActionId]);

    function submit() {
        if (formData) {
            DataService
                .update<WorkflowActionModel>(`api/v1/WorkflowActions/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'workflowTaskDefinitionId', label: 'Task' },
            { name: 'nextWorkflowStepDefinitionId', label: 'Next step', validationRules: [{ type: 'isRequired' }] },
            { name: 'buttonOrder', label: 'Display order', validationRules: [{ type: 'isRequired' }] },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!workflowActionId || !formData)
        return <SpinnerOverlay />

    const { isAuthorizedForWorkflowDefinition } = useAuth();
    if (!isAuthorizedForWorkflowDefinition(ApplicationFunctions.ViewWorkflowDefinition, formData.workflowStepDefinition.workflowDefinitionId))
        return (<Unauthorized />);

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem width={275} icon="fa fa-gears" title="GoTo workflow definition" onClick={() => navigate('/plm/WorkflowSetup/Workflow/' + formData.workflowStepDefinition.workflowDefinitionId)} />
            <ActionMenuItem icon="fa fa-gears" title="GoTo workflow step definition" onClick={() => navigate('/plm/WorkflowSetup/WorkflowStep/' + formData.workflowStepDefinitionId)} />
            <ActionMenuItem icon="fa fa-gears" title="GoTo workflow next step definition" onClick={() => navigate('/plm/WorkflowSetup/WorkflowStep/' + formData.nextWorkflowStepDefinitionId)} />
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='Action' actions={formActions} footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.UpdateWorkflowDefinition} workflowDefinitionId={formData.workflowStepDefinition.workflowDefinitionId}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <SelectListField {...getFieldProps('workflowTaskDefinitionId')} options={workflowTaskDefinitions} />
                            <SelectListField {...getFieldProps('nextWorkflowStepDefinitionId')} options={workflowStepDefinitions} />
                            <NumberField {...getFieldProps('buttonOrder')} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                    </Row>
                </Container>
            </Form>
            <Container fluid>
                <Row>
                    <Col md='12'>
                        <HorizontalTabs
                            id='vtabs'
                            defaultActiveKey='tasks'
                            tabs={[
                                {
                                    title: 'Code tasks',
                                    eventKey: 'tasks',
                                    component: <Action_tab_CodeTasks workflowActionId={workflowActionId} ></Action_tab_CodeTasks>,
                                },
                            ]}
                        />
                    </Col>
                </Row>
            </Container>

        </div>
    );
}
export default WorkflowActionView;